import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  FilterChangedEvent,
  GridApi,
  RowClickedEvent,
} from "ag-grid-community";
import { BudgetAndAlertsAgGridToolbar } from "../common/BudgetAndAlertsAgGridToolbar";
import { Budget } from "@/services/cloudchipr.api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { AgDataGrid } from "@/components/pages/common/ag-grid/AgDataGrid";
import { budgetsGridColumns } from "@/components/pages/budgets-and-alerts/components/grids/budgets/components/budgetsGridColumns";
import { budgetAndAlertsDefaultColDef } from "@/components/pages/budgets-and-alerts/components/grids/common/utils/constants/constants";
import { BudgetsGridNoRowsComponent } from "@/components/pages/budgets-and-alerts/components/grids/budgets/components/BudgetsGridNoRowsComponent";
import { budgetGridDataSelector } from "@/store/budgets/selectors/budgetGridDataSelector";
import { BudgetWithIntegrations } from "@/store/budgets/utils/types/common";
import { setBudgetDetailsDrawerBudgetId } from "@/store/budgets/budgetsSlice";

export const BudgetsGrid: FC = () => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const dispatch = useAppDispatch();
  const data = useAppSelector(budgetGridDataSelector);

  const handleNoRowsOverlayVisibility = useCallback(
    (gridApi: GridApi | null) => {
      if (!gridApi) {
        return;
      }

      if (!gridApi.getDisplayedRowCount()) {
        gridApi?.showNoRowsOverlay();
      } else {
        gridApi?.hideOverlay();
      }
    },
    [],
  );

  const filterChangeHandler = useCallback(
    (event: FilterChangedEvent<Budget>) => {
      handleNoRowsOverlayVisibility(event.api);
    },
    [handleNoRowsOverlayVisibility],
  );

  const rowClickHandler = useCallback(
    ({ data }: RowClickedEvent<BudgetWithIntegrations>) => {
      if (!data) {
        return;
      }

      dispatch(setBudgetDetailsDrawerBudgetId(data.id));
    },
    [dispatch],
  );

  useEffect(() => {
    handleNoRowsOverlayVisibility(gridApi);
  }, [data?.length, gridApi, handleNoRowsOverlayVisibility]);

  return (
    <Fragment>
      <BudgetAndAlertsAgGridToolbar gridApi={gridApi} />

      <AgDataGrid
        rowHeight={58}
        headerHeight={56}
        rowData={data ?? []}
        gridApiSetter={setGridApi}
        onRowClicked={rowClickHandler}
        columnDefs={budgetsGridColumns}
        onFilterChanged={filterChangeHandler}
        defaultColDef={budgetAndAlertsDefaultColDef}
        noRowsOverlayComponent={BudgetsGridNoRowsComponent}
        configToRememberStateInStorage={configToRememberStateInStorage}
      />
    </Fragment>
  );
};

const configToRememberStateInStorage = {
  uniqueNameAsKey: "budgetsDataGrid",
};
